import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Wallet, CreditCard, Copy, ArrowLeftRight } from 'lucide-react';
import {
  adminApi,
  Client,
  ClientAccountListItem,
  CurrencyDto,
  ClientTier,
} from '../api/admin';
import { portalClientsApi, type ClientReminderDto, type ComplianceNoteDto } from '../api/portalClients';
import { getErrorMessage } from '../api/client';
import { notifyError, notifySuccess, notifyErrorMessage, notifyInfo } from '../utils/notify';
import { formatPublicIdDisplay } from '../utils/formatPublicId';

interface ClientDetailsModalProps {
  client: Client;
  onClose: () => void;
  onTierUpdated?: (clientId: string, tierId: string | null, tier?: ClientTier) => void;
  onEmailUpdated?: (clientId: string, email: string) => void;
}

function ClientDetailsModal({ client, onClose, onTierUpdated, onEmailUpdated }: ClientDetailsModalProps) {
  const [accounts, setAccounts] = useState<ClientAccountListItem[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Open account form
  const [openFormVisible, setOpenFormVisible] = useState(false);
  const [newCurrency, setNewCurrency] = useState('');
  const [opening, setOpening] = useState(false);

  // Top up form
  const [topUpAccountId, setTopUpAccountId] = useState<string | null>(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpReason, setTopUpReason] = useState('');
  const [topUpComment, setTopUpComment] = useState('');
  const [toppingUp, setToppingUp] = useState(false);

  const [rebalanceVisible, setRebalanceVisible] = useState(false);
  const [rebalanceFromId, setRebalanceFromId] = useState('');
  const [rebalanceToId, setRebalanceToId] = useState('');
  const [rebalanceAmount, setRebalanceAmount] = useState('');
  const [rebalanceReason, setRebalanceReason] = useState('');
  const [rebalanceComment, setRebalanceComment] = useState('');
  const [rebalancing, setRebalancing] = useState(false);

  const [complianceNotes, setComplianceNotes] = useState<ComplianceNoteDto[]>([]);
  const [clientReminders, setClientReminders] = useState<ClientReminderDto[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderDescription, setReminderDescription] = useState('');
  const [reminderDue, setReminderDue] = useState('');
  const [currentEmail, setCurrentEmail] = useState(client.email || '');
  const [emailDraft, setEmailDraft] = useState(client.email || '');
  const [emailBusy, setEmailBusy] = useState(false);
  const [statusDraft, setStatusDraft] = useState(client.status || 'Unactive');
  const [statusReason, setStatusReason] = useState('');
  const [currentPortalStatus, setCurrentPortalStatus] = useState(client.status || 'Unactive');
  const [tierDraft, setTierDraft] = useState<string | ''>(client.tierId || '');
  const [tiers, setTiers] = useState<ClientTier[]>([]);
  const [tierBusy, setTierBusy] = useState(false);
  const [complianceBusy, setComplianceBusy] = useState(false);
  const [digitalIncomingEnabled, setDigitalIncomingEnabled] = useState(client.digitalIncomingEnabled ?? false);
  const [digitalOutgoingEnabled, setDigitalOutgoingEnabled] = useState(client.digitalOutgoingEnabled ?? false);
  const [digitalFeaturesBusy, setDigitalFeaturesBusy] = useState(false);
  const [passwordDelivery, setPasswordDelivery] = useState<'email' | 'show'>('email');
  const [regeneratedPassword, setRegeneratedPassword] = useState<string | null>(null);

  useEffect(() => {
    const initialEmail = (client.email || '').trim();
    setCurrentEmail(initialEmail);
    setEmailDraft(initialEmail);
  }, [client.id, client.email]);

  useEffect(() => {
    const next = client.status || 'Unactive';
    setStatusDraft(next);
    setCurrentPortalStatus(next);
    setStatusReason('');
  }, [client.id, client.status]);

  useEffect(() => {
    setTierDraft(client.tierId || '');
  }, [client.tierId]);

  useEffect(() => {
    setDigitalIncomingEnabled(client.digitalIncomingEnabled ?? false);
    setDigitalOutgoingEnabled(client.digitalOutgoingEnabled ?? false);
  }, [client.id, client.digitalIncomingEnabled, client.digitalOutgoingEnabled]);

  // Load tiers for the dropdown and fetch current tier from server
  useEffect(() => {
    const loadTiers = async () => {
      try {
        const response = await adminApi.getAllTiers();
        setTiers(response.data || []);
      } catch {
        // Silent fail - tiers not critical for viewing client
      }
    };
    const loadCurrentTier = async () => {
      try {
        const res = await adminApi.getClientTier(client.id);
        setTierDraft(res.data?.tierId || '');
      } catch {
        // fallback to value from client prop
      }
    };
    loadTiers();
    loadCurrentTier();
  }, [client.id]);

  useEffect(() => {
    setRegeneratedPassword(null);
    setPasswordDelivery('email');
  }, [client.id]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [accountsRes, currenciesRes] = await Promise.all([
        adminApi.getClientAccountsByClientId(client.id),
        adminApi.getOpenableClientCurrencies(client.id)
          .catch(() => adminApi.getCurrencies())
          .catch(() => ({ data: [] as CurrencyDto[] })),
      ]);
      setAccounts(accountsRes.data || []);
      setCurrencies(currenciesRes.data || []);
      try {
        const [n, r] = await Promise.all([
          portalClientsApi.listNotes(client.id),
          portalClientsApi.listReminders(client.id),
        ]);
        setComplianceNotes(n);
        setClientReminders(r);
      } catch {
        setComplianceNotes([]);
        setClientReminders([]);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load client data'));
    } finally {
      setLoading(false);
    }
  };

  const applyDigitalFeatures = async () => {
    try {
      setDigitalFeaturesBusy(true);
      setError('');
      await portalClientsApi.setDigitalFeatures(client.id, digitalIncomingEnabled, digitalOutgoingEnabled);
      notifySuccess('OFT features updated.');
    } catch (e: unknown) {
      notifyError(e, 'Failed to update OFT features');
    } finally {
      setDigitalFeaturesBusy(false);
    }
  };

  const applyPortalEmail = async () => {
    const nextEmail = emailDraft.trim().toLowerCase();
    if (!nextEmail) {
      setError('Email is required');
      return;
    }
    try {
      setEmailBusy(true);
      setError('');
      await portalClientsApi.updateEmail(client.id, nextEmail);
      setCurrentEmail(nextEmail);
      setEmailDraft(nextEmail);
      onEmailUpdated?.(client.id, nextEmail);
      notifySuccess('Client email updated.');
    } catch (e: unknown) {
      notifyError(e, 'Failed to update client email');
    } finally {
      setEmailBusy(false);
    }
  };

  const statusReasonRequired = useMemo(() => {
    const next = statusDraft.trim();
    const prev = currentPortalStatus.trim();
    return (
      next === 'Restricted' ||
      next === 'Closed' ||
      (prev === 'Restricted' && next === 'Active')
    );
  }, [statusDraft, currentPortalStatus]);

  const applyPortalStatus = async () => {
    const reason = statusReason.trim();
    if (statusReasonRequired && !reason) {
      notifyErrorMessage('Reason is required for this status change.');
      return;
    }
    try {
      setComplianceBusy(true);
      setError('');
      await portalClientsApi.setStatus(client.id, statusDraft, reason || undefined);
      setCurrentPortalStatus(statusDraft);
      setStatusReason('');
      notifySuccess('Portal user status updated.');
    } catch (e: unknown) {
      notifyError(e, 'Failed to update status');
    } finally {
      setComplianceBusy(false);
    }
  };

  const applyTier = async () => {
    try {
      setTierBusy(true);
      setError('');
      const tierId = tierDraft || null;
      await adminApi.updateClientTier(client.id, tierId, 'Updated by admin from client details');
      const tierName = tierId ? tiers.find(t => t.id === tierId)?.name || 'Unknown' : 'No Tier';
      notifySuccess(`Client tier updated to ${tierName}.`);
      // Sync tierDraft from server to confirm persisted value
      try {
        const res = await adminApi.getClientTier(client.id);
        const persistedTierId = res.data?.tierId || '';
        setTierDraft(persistedTierId);
        onTierUpdated?.(client.id, persistedTierId || null, res.data?.tier);
      } catch {
        onTierUpdated?.(client.id, tierId, tierId ? tiers.find(t => t.id === tierId) : undefined);
        // keep optimistic value in tierDraft
      }
    } catch (e: unknown) {
      notifyError(e, 'Failed to update tier');
    } finally {
      setTierBusy(false);
    }
  };

  const addComplianceNote = async () => {
    const t = noteDraft.trim();
    if (!t) return;
    try {
      setComplianceBusy(true);
      await portalClientsApi.addNote(client.id, t);
      setNoteDraft('');
      const n = await portalClientsApi.listNotes(client.id);
      setComplianceNotes(n);
      notifySuccess('Note saved');
    } catch (e: unknown) {
      notifyError(e, 'Failed to add note');
    } finally {
      setComplianceBusy(false);
    }
  };

  const addClientReminder = async () => {
    const title = reminderTitle.trim();
    if (!title || !reminderDue) {
      setError('Reminder title and due date are required');
      return;
    }
    try {
      setComplianceBusy(true);
      setError('');
      const dueAt = new Date(reminderDue).toISOString();
      await portalClientsApi.addReminder(client.id, {
        title,
        description: reminderDescription.trim() || null,
        dueAt,
      });
      setReminderTitle('');
      setReminderDescription('');
      setReminderDue('');
      const r = await portalClientsApi.listReminders(client.id);
      setClientReminders(r);
      notifySuccess('Reminder created');
    } catch (e: unknown) {
      notifyError(e, 'Failed to add reminder');
    } finally {
      setComplianceBusy(false);
    }
  };

  const toggleReminder = async (reminderId: string, completed: boolean) => {
    try {
      setComplianceBusy(true);
      await portalClientsApi.setReminderCompleted(client.id, reminderId, completed);
      const r = await portalClientsApi.listReminders(client.id);
      setClientReminders(r);
    } catch (e: unknown) {
      notifyError(e, 'Failed to update reminder');
    } finally {
      setComplianceBusy(false);
    }
  };

  const regeneratePortalPassword = async () => {
    try {
      setComplianceBusy(true);
      setError('');
      setRegeneratedPassword(null);
      const res = await portalClientsApi.regeneratePassword(client.id, passwordDelivery);
      if (res.plainPassword) {
        setRegeneratedPassword(res.plainPassword);
      }
      if (passwordDelivery === 'email') {
        if (res.emailSent) {
          notifySuccess("New password sent to the client's email.");
        } else if (res.plainPassword) {
          notifyErrorMessage(
            'Password was updated but the email could not be sent. Copy the password below and share it with the client securely.',
          );
        } else {
          notifyInfo('Password was updated but the email could not be sent and the password cannot be shown.');
        }
      } else {
        notifySuccess('Password generated. Save or copy it now — it will not be shown again after you close this dialog.');
      }
    } catch (e: unknown) {
      notifyError(e, 'Failed to regenerate password');
    } finally {
      setComplianceBusy(false);
    }
  };

  const copyRegeneratedPassword = async () => {
    if (!regeneratedPassword) return;
    try {
      await navigator.clipboard.writeText(regeneratedPassword);
      notifySuccess('Password copied');
    } catch {
      notifyErrorMessage('Could not copy to clipboard');
    }
  };

  const handleOpenAccount = async () => {
    if (!newCurrency) {
      setError('Please select a currency');
      return;
    }
    try {
      setOpening(true);
      setError('');
      await adminApi.openClientAccount({
        clientId: client.id,
        currencyId: newCurrency,
      });
      setNewCurrency('');
      setOpenFormVisible(false);
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to open account'));
    } finally {
      setOpening(false);
    }
  };

  const handleTopUp = async (accountId: string) => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount <= 0) {
      setError('Amount must be greater than zero');
      return;
    }
    if (!topUpReason.trim()) {
      setError('Reason is required');
      return;
    }
    try {
      setToppingUp(true);
      setError('');
      await adminApi.topUpClientAccount({
        clientId: client.id,
        clientAccountId: accountId,
        amount,
        reason: topUpReason,
        comment: topUpComment || undefined,
      });
      setTopUpAccountId(null);
      setTopUpAmount('');
      setTopUpReason('');
      setTopUpComment('');
      await loadData();
      notifySuccess('Top-up credited to Available and shown in client Incoming payments.');
    } catch (err: unknown) {
      notifyError(err, 'Failed to top up account');
    } finally {
      setToppingUp(false);
    }
  };

  const accountLabel = (account: ClientAccountListItem) => {
    const currency = resolveCurrencyLabel(account.currencyId);
    const provider = account.providerName || account.providerId?.slice(0, 8) || '—';
    const bal = account.availableBalance?.toFixed(2) ?? '0.00';
    return `${currency} · ${provider} · Available ${bal}`;
  };

  const rebalanceFromAccount = accounts.find((a) => a.id === rebalanceFromId);
  const rebalanceToOptions = accounts.filter((a) => {
    if (!rebalanceFromAccount) return a.id !== rebalanceFromId;
    return (
      a.id !== rebalanceFromId &&
      a.providerId !== rebalanceFromAccount.providerId &&
      String(a.currencyId).toUpperCase() === String(rebalanceFromAccount.currencyId).toUpperCase()
    );
  });

  const handleRebalance = async () => {
    const amount = parseFloat(rebalanceAmount);
    if (!rebalanceFromId || !rebalanceToId) {
      setError('Select source and destination accounts');
      return;
    }
    if (!amount || amount <= 0) {
      setError('Amount must be greater than zero');
      return;
    }
    if (!rebalanceReason.trim()) {
      setError('Reason is required');
      return;
    }
    try {
      setRebalancing(true);
      setError('');
      await adminApi.rebalanceClientAccounts({
        clientId: client.id,
        fromClientAccountId: rebalanceFromId,
        toClientAccountId: rebalanceToId,
        amount,
        reason: rebalanceReason,
        comment: rebalanceComment || undefined,
      });
      setRebalanceFromId('');
      setRebalanceToId('');
      setRebalanceAmount('');
      setRebalanceReason('');
      setRebalanceComment('');
      setRebalanceVisible(false);
      await loadData();
      notifySuccess('Transfer posted (Available → Available, fee 0).');
    } catch (err: unknown) {
      notifyError(err, 'Failed to transfer between accounts');
    } finally {
      setRebalancing(false);
    }
  };

  const availableCurrencies = currencies.filter(
    (c) => !accounts.some((a) => a.currencyId.toUpperCase() === c.code.toUpperCase()),
  );

  const currencyCodeByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of currencies) {
      if (!c?.id || !c?.code) continue;
      map.set(c.id.toUpperCase(), c.code.toUpperCase());
      map.set(c.code.toUpperCase(), c.code.toUpperCase());
    }
    return map;
  }, [currencies]);

  const resolveCurrencyLabel = (raw: string | null | undefined) => {
    const value = (raw || '').trim();
    if (!value) return '';
    return currencyCodeByKey.get(value.toUpperCase()) || value.toUpperCase();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-lg bg-white mb-10">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Client Details</h3>
            <p className="text-sm text-gray-500 mt-1">{currentEmail}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-red-800 text-sm">{String(error)}</p>
          </div>
        )}

        {/* Client info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">General Info</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Internal ID:</span>{' '}
              <span className="text-gray-900 font-mono text-xs break-all">{client.id}</span>
            </div>
            <div>
              <span className="text-gray-500">USER ID:</span>{' '}
              {client.publicId
                ? <span className="text-indigo-700 font-mono font-semibold">{formatPublicIdDisplay(client.publicId)}</span>
                : <span className="text-gray-400 text-xs">—</span>
              }
            </div>
            <div>
              <span className="text-gray-500">Email:</span>{' '}
              <span className="text-gray-900">{currentEmail}</span>
            </div>
            <div>
              <span className="text-gray-500">Company:</span>{' '}
              <span className="text-gray-900 break-words">{client.companyName?.trim() ? client.companyName.trim() : '—'}</span>
            </div>
            <div>
              <span className="text-gray-500">Name:</span>{' '}
              <span className="text-gray-900">
                {client.firstName || ''} {client.lastName || ''}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>{' '}
              <span className="text-gray-900">{currentPortalStatus}</span>
            </div>
            <div>
              <span className="text-gray-500">Email Confirmed:</span>{' '}
              <span className="text-gray-900">{client.emailConfirmed ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-gray-500">2FA:</span>{' '}
              <span className="text-gray-900">{client.twoFactorEnabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            <div>
              <span className="text-gray-500">Created:</span>{' '}
              <span className="text-gray-900">
                {client.createdAt && new Date(client.createdAt).getFullYear() > 2000
                  ? new Date(client.createdAt).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Last login:</span>{' '}
              <span className="text-gray-900">
                {client.lastLoginAt ? new Date(client.lastLoginAt).toLocaleString() : 'Never'}
              </span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-xs font-medium text-gray-600 mb-1">Change email</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                disabled={emailBusy}
                className="flex-1 min-w-[240px] px-3 py-2 border rounded-lg text-sm border-gray-300"
                placeholder="client@example.com"
              />
              <button
                type="button"
                disabled={emailBusy || !emailDraft.trim() || emailDraft.trim().toLowerCase() === currentEmail.toLowerCase()}
                onClick={() => void applyPortalEmail()}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {emailBusy ? 'Saving...' : 'Update email'}
              </button>
            </div>
          </div>
        </div>

        {/* Compliance — notes, reminders, lifecycle */}
        <div className="rounded-lg border border-gray-200 p-4 mb-6">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Compliance & lifecycle</h4>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Portal user status</label>
              <select
                value={statusDraft}
                onChange={(e) => setStatusDraft(e.target.value)}
                disabled={complianceBusy}
                className="px-3 py-2 border rounded-lg text-sm border-gray-300 min-w-[200px]"
              >
                <option value="Unactive">Unactive</option>
                <option value="Active">Active</option>
                <option value="Restricted">Restricted</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Reason{statusReasonRequired ? ' *' : ''}
              </label>
              <input
                type="text"
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                disabled={complianceBusy}
                placeholder={
                  statusReasonRequired
                    ? 'Required for Restricted, Closed, or lifting restriction'
                    : 'Optional'
                }
                className="w-full px-3 py-2 border rounded-lg text-sm border-gray-300"
              />
            </div>
            <button
              type="button"
              disabled={complianceBusy}
              onClick={() => void applyPortalStatus()}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              Apply status
            </button>
          </div>

          {/* Digital Features */}
          <div className="flex flex-wrap items-end gap-3 mb-4 border-t border-gray-100 pt-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-2">OFT Payment Features</label>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={digitalIncomingEnabled}
                    onChange={(e) => setDigitalIncomingEnabled(e.target.checked)}
                    disabled={digitalFeaturesBusy}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">OFT Incoming</span>
                  <span className="text-xs text-gray-400">(admin only)</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={digitalOutgoingEnabled}
                    onChange={(e) => setDigitalOutgoingEnabled(e.target.checked)}
                    disabled={digitalFeaturesBusy}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">OFT Outgoing</span>
                  <span className="text-xs text-gray-400">(admin only)</span>
                </label>
              </div>
            </div>
            <button
              type="button"
              disabled={digitalFeaturesBusy}
              onClick={() => void applyDigitalFeatures()}
              className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {digitalFeaturesBusy ? 'Saving...' : 'Save features'}
            </button>
          </div>

          {/* Client Tier */}
          <div className="flex flex-wrap items-end gap-3 mb-4 border-t border-gray-100 pt-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client Tier</label>
              <select
                value={tierDraft}
                onChange={(e) => setTierDraft(e.target.value)}
                disabled={tierBusy}
                className="px-3 py-2 border rounded-lg text-sm border-gray-300 min-w-[200px]"
              >
                <option value="">No Tier (Default Pricing)</option>
                {tiers.filter(t => t.isActive).map(tier => (
                  <option key={tier.id} value={tier.id}>{tier.name}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={tierBusy}
              onClick={() => void applyTier()}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              Apply tier
            </button>
            <span className="text-xs text-gray-500">
              Tier affects fee calculations for this client
            </span>
          </div>

          <div className="border-t border-gray-100 pt-4 mt-4">
            <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Portal login password
            </h5>
            <p className="text-xs text-gray-600 mb-3">
              Generate a new password. The client's current portal sessions will be revoked.
            </p>
            <div className="flex flex-wrap gap-4 mb-3 text-sm">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="password-delivery"
                  checked={passwordDelivery === 'email'}
                  onChange={() => setPasswordDelivery('email')}
                  disabled={complianceBusy}
                  className="rounded-full border-gray-300 text-indigo-600"
                />
                <span>Send to client's email</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="password-delivery"
                  checked={passwordDelivery === 'show'}
                  onChange={() => setPasswordDelivery('show')}
                  disabled={complianceBusy}
                  className="rounded-full border-gray-300 text-indigo-600"
                />
                <span>Show on screen</span>
              </label>
            </div>
            <button
              type="button"
              disabled={complianceBusy}
              onClick={() => void regeneratePortalPassword()}
              className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              Generate password
            </button>
            {regeneratedPassword ? (
              <div className="mt-3 flex flex-wrap items-stretch gap-2">
                <input
                  readOnly
                  value={regeneratedPassword}
                  className="flex-1 min-w-[200px] px-3 py-2 border rounded-lg text-sm font-mono border-amber-200 bg-amber-50"
                />
                <button
                  type="button"
                  onClick={() => void copyRegeneratedPassword()}
                  className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
              </div>
            ) : null}
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Internal notes</h5>
              <ul className="max-h-40 overflow-auto space-y-2 text-sm mb-2">
                {complianceNotes.map((n) => (
                  <li key={n.id} className="border border-gray-100 rounded p-2 bg-gray-50 whitespace-pre-wrap">
                    {n.body}
                    <div className="text-[10px] text-gray-400 mt-1">
                      {new Date(n.createdAt).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={3}
                className="w-full border rounded-lg px-3 py-2 text-sm border-gray-300"
                placeholder="Compliance note…"
              />
              <button
                type="button"
                disabled={complianceBusy || !noteDraft.trim()}
                onClick={() => void addComplianceNote()}
                className="mt-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg disabled:opacity-50"
              >
                Add note
              </button>
            </div>
            <div>
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Reminders</h5>
              <ul className="max-h-40 overflow-auto space-y-2 text-sm mb-2">
                {clientReminders.map((r) => (
                  <li
                    key={r.id}
                    className={`flex items-start justify-between gap-2 border rounded p-2 text-sm ${
                      r.completedAt ? 'bg-green-50 border-green-100' : 'bg-white border-gray-100'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{r.title}</p>
                      {r.description ? (
                        <p className="text-gray-600 text-xs mt-1 whitespace-pre-wrap">{r.description}</p>
                      ) : null}
                      <p className="text-[10px] text-gray-500 mt-1">Due: {new Date(r.dueAt).toLocaleString()}</p>
                    </div>
                    <button
                      type="button"
                      disabled={complianceBusy}
                      onClick={() => void toggleReminder(r.id, !r.completedAt)}
                      className="shrink-0 text-xs text-indigo-600 hover:underline disabled:opacity-50"
                    >
                      {r.completedAt ? 'Reopen' : 'Done'}
                    </button>
                  </li>
                ))}
              </ul>
              <input
                type="text"
                value={reminderTitle}
                onChange={(e) => setReminderTitle(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm border-gray-300 mb-2"
                placeholder="Title"
              />
              <input
                type="datetime-local"
                value={reminderDue}
                onChange={(e) => setReminderDue(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm border-gray-300 mb-2"
              />
              <textarea
                value={reminderDescription}
                onChange={(e) => setReminderDescription(e.target.value)}
                rows={2}
                className="w-full border rounded-lg px-3 py-2 text-sm border-gray-300 mb-2"
                placeholder="Description (optional)"
              />
              <button
                type="button"
                disabled={complianceBusy}
                onClick={() => void addClientReminder()}
                className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg disabled:opacity-50"
              >
                Add reminder
              </button>
            </div>
          </div>
        </div>

        {/* Accounts */}
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center">
            <Wallet className="h-5 w-5 mr-2" /> Accounts ({accounts.length})
          </h4>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRebalanceVisible((v) => !v)}
              disabled={accounts.length < 2}
              className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <ArrowLeftRight className="h-4 w-4 mr-1" /> Transfer between accounts
            </button>
            <button
              onClick={() => setOpenFormVisible((v) => !v)}
              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-1" /> Open Account
            </button>
          </div>
        </div>

        {rebalanceVisible && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-indigo-900 mb-3">
              Move Available balance between this client&apos;s accounts on different providers. Same currency, fee 0.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                <select
                  value={rebalanceFromId}
                  onChange={(e) => {
                    setRebalanceFromId(e.target.value);
                    setRebalanceToId('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- Select --</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {accountLabel(a)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                <select
                  value={rebalanceToId}
                  onChange={(e) => setRebalanceToId(e.target.value)}
                  disabled={!rebalanceFromId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <option value="">-- Select --</option>
                  {rebalanceToOptions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {accountLabel(a)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Amount
                  {rebalanceFromAccount
                    ? ` (${resolveCurrencyLabel(rebalanceFromAccount.currencyId)})`
                    : ''}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rebalanceAmount}
                  onChange={(e) => setRebalanceAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="100.00"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                <input
                  type="text"
                  value={rebalanceReason}
                  onChange={(e) => setRebalanceReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Provider rebalance"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Comment (optional)</label>
                <textarea
                  value={rebalanceComment}
                  onChange={(e) => setRebalanceComment(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => setRebalanceVisible(false)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleRebalance()}
                disabled={rebalancing}
                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {rebalancing ? 'Processing...' : 'Confirm transfer'}
              </button>
            </div>
          </div>
        )}

        {openFormVisible && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={newCurrency}
                  onChange={(e) => setNewCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Select --</option>
                  {availableCurrencies.map((c) => (
                    <option key={c.id} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleOpenAccount}
                disabled={opening}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {opening ? 'Opening...' : 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setOpenFormVisible(false);
                  setNewCurrency('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
            No accounts yet
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((account) => (
              <div key={account.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <CreditCard className="h-5 w-5 mr-2 text-blue-600" />
                    <div>
                      <div className="font-semibold text-gray-900">
                        {resolveCurrencyLabel(account.currencyId)} {account.accountName ? `— ${account.accountName}` : ''}
                        {account.providerName ? (
                          <span className="ml-2 text-xs font-normal text-gray-500">({account.providerName})</span>
                        ) : null}
                      </div>
                      {account.accountNumber && (
                        <div className="text-xs text-gray-500 font-mono">{account.accountNumber}</div>
                      )}
                      {account.bankName && (
                        <div className="text-xs text-gray-400">{account.bankName}{account.swiftCode ? ` · ${account.swiftCode}` : ''}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        account.status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {account.status}
                    </span>
                    <button
                      onClick={() =>
                        setTopUpAccountId(topUpAccountId === account.id ? null : account.id)
                      }
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                    >
                      Top Up
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                  <div>Available: <span className="font-semibold text-gray-900">{account.availableBalance?.toFixed(2) ?? '—'} {resolveCurrencyLabel(account.currencyId)}</span></div>
                  <div>Blocked: <span className="font-semibold text-gray-900">{account.blockedBalance?.toFixed(2) ?? '—'} {resolveCurrencyLabel(account.currencyId)}</span></div>
                </div>

                {topUpAccountId === account.id && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Amount ({resolveCurrencyLabel(account.currencyId)})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={topUpAmount}
                          onChange={(e) => setTopUpAmount(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="100.00"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                        <input
                          type="text"
                          value={topUpReason}
                          onChange={(e) => setTopUpReason(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Manual adjustment"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Comment (optional)
                        </label>
                        <textarea
                          value={topUpComment}
                          onChange={(e) => setTopUpComment(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      <button
                        onClick={() => setTopUpAccountId(null)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleTopUp(account.id)}
                        disabled={toppingUp}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {toppingUp ? 'Processing...' : 'Confirm Top Up'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ClientDetailsModal;
